import { useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  Menu,
  MenuItem,
  Radio,
  Typography,
} from "@mui/material";
import { DragIndicator } from "@mui/icons-material";
import { Copy, Eye, EyeOff, Layers, Lock, LockOpen, Trash2 } from "lucide-react";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  BUTTON_ANALYZE,
  BUTTON_TRY_AGAIN,
  MODAL_IMAGE_EDIT_LAYERS_EMPTY_TITLE,
  MODAL_IMAGE_EDIT_LAYERS_EMPTY_DESCRIPTION,
  MODAL_IMAGE_EDIT_LAYERS_ERROR_TITLE,
  MODAL_IMAGE_EDIT_LAYERS_ERROR_DESCRIPTION,
  MODAL_IMAGE_EDIT_LAYERS_ADD,
  MODAL_IMAGE_EDIT_LAYERS_LIST_TITLE,
  MODAL_IMAGE_EDIT_LAYERS_BACKGROUND,
  MODAL_IMAGE_EDIT_LAYERS_FALLBACK_NAME,
  MODAL_IMAGE_EDIT_LAYERS_ANALYZING,
  TEMPLATES_DELETE,
  TEMPLATES_DUPLICATE,
  TEMPLATES_LOCK_LAYER,
  TEMPLATES_UNLOCK_LAYER,
  TEMPLATES_HIDE_LAYER,
  TEMPLATES_SHOW_LAYER,
  TEMPLATES_PRODUCE_ADD_IMAGE,
  TEMPLATES_PRODUCE_ADD_TEXT,
} from "../../../../i18n/keysTranslations";
import Button from "../../../Buttons/Button";
import ModalGalleryAssets from "../../ModalGalleryAssets";
import { LayerNameEditor } from "../shared";
import { LAYER_TYPE, getLayerType } from "./layerGeometry";
import {
  layerListNoNativeSelectSx,
  preventLayerListNativeSelectStart,
  preventNativeTextSelectOnAdditivePointer,
} from "../../../../features/creativeTemplates/editor/canvasLayerSelection";
import {
  layerListAutoScroll,
  sortableLayerRowStyle,
} from "../../../../features/creativeTemplates/editor/layerListDnd";
import { useTranslationApp } from "../../../../lib/i18next";

const SELECTED_BORDER = "#111";

const rowIconButtonSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "rgba(0,0,0,0.45)",
  cursor: "pointer",
  flexShrink: 0,
  border: 0,
  bgcolor: "transparent",
  p: 0.25,
  "&:hover": { color: "rgba(0,0,0,0.8)" },
};

const dragHandleSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "rgba(0,0,0,0.28)",
  touchAction: "none",
  userSelect: "none",
  WebkitUserSelect: "none",
  WebkitUserDrag: "none",
  flexShrink: 0,
  minWidth: 32,
  minHeight: 40,
  px: 0.5,
  ml: -0.5,
};

const LayerDragHandle = ({
  handleRef,
  attributes = {},
  listeners = {},
  isDragging = false,
  isOverlay = false,
}) => (
  <Box
    component="span"
    ref={handleRef}
    {...attributes}
    {...listeners}
    onClick={isOverlay ? undefined : (event) => event.stopPropagation()}
    onDragStart={isOverlay ? undefined : (event) => event.preventDefault()}
    sx={{
      ...dragHandleSx,
      cursor: isDragging || isOverlay ? "grabbing" : "grab",
    }}
  >
    <DragIndicator fontSize="small" sx={{ pointerEvents: "none" }} />
  </Box>
);

const LayerRowContent = ({
  layer,
  name,
  selected,
  isDragging = false,
  isOverlay = false,
  handleRef,
  handleAttributes,
  handleListeners,
  onSelect,
  onRename,
  onDuplicate,
  onDelete,
  onToggleLock,
  onToggleVisible,
  duplicateLabel,
  deleteLabel,
  lockLabel,
  unlockLabel,
  hideLabel,
  showLabel,
}) => (
  <>
    <LayerDragHandle
      handleRef={handleRef}
      attributes={handleAttributes}
      listeners={handleListeners}
      isDragging={isDragging}
      isOverlay={isOverlay}
    />
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: 1,
        overflow: "hidden",
        bgcolor: "grey.100",
        flexShrink: 0,
        border: "1px solid rgba(0,0,0,0.06)",
        userSelect: "none",
        WebkitUserSelect: "none",
        pointerEvents: "none",
      }}
    >
      {getLayerType(layer) === LAYER_TYPE.TEXT ? (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 0.25,
            color: "#111",
            fontSize: 9,
            fontWeight: 700,
            overflow: "hidden",
          }}
        >
          Aa
        </Box>
      ) : layer.url ? (
        <Box
          component="img"
          src={layer.url}
          alt={name}
          draggable={false}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
            WebkitUserDrag: "none",
          }}
        />
      ) : null}
    </Box>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      {isOverlay ? (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            color: "text.primary",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            lineHeight: 1.3,
          }}
        >
          {name}
        </Typography>
      ) : (
        <LayerNameEditor
          name={name}
          onStartEdit={() => onSelect(layer.id)}
          onCommit={(nextName) => {
            const trimmed = String(nextName || "").trim();
            if (trimmed === name && (!layer.name || layer.name === trimmed)) {
              return;
            }
            onRename(layer.id, trimmed);
          }}
        />
      )}
    </Box>
    {layer.isBackground ? null : (
      <Box
        component="button"
        type="button"
        title={duplicateLabel}
        aria-label={duplicateLabel}
        tabIndex={isOverlay ? -1 : undefined}
        onClick={
          isOverlay
            ? undefined
            : (event) => {
                event.stopPropagation();
                onDuplicate(layer.id);
              }
        }
        sx={rowIconButtonSx}
      >
        <Copy size={15} />
      </Box>
    )}
    {onToggleVisible ? (
      <Box
        component="button"
        type="button"
        title={layer.visible !== false ? hideLabel : showLabel}
        aria-label={layer.visible !== false ? hideLabel : showLabel}
        tabIndex={isOverlay ? -1 : undefined}
        onClick={
          isOverlay
            ? undefined
            : (event) => {
                event.stopPropagation();
                onToggleVisible(layer.id, layer.visible === false);
              }
        }
        sx={{
          ...rowIconButtonSx,
          color:
            layer.visible !== false ? "rgba(0,0,0,0.45)" : "rgba(0,0,0,0.8)",
        }}
      >
        {layer.visible !== false ? <Eye size={15} /> : <EyeOff size={15} />}
      </Box>
    ) : null}
    {layer.isBackground ? null : (
      <>
        {onToggleLock ? (
          <Box
            component="button"
            type="button"
            title={layer.locked ? unlockLabel : lockLabel}
            aria-label={layer.locked ? unlockLabel : lockLabel}
            tabIndex={isOverlay ? -1 : undefined}
            onClick={
              isOverlay
                ? undefined
                : (event) => {
                    event.stopPropagation();
                    onToggleLock(layer.id, !layer.locked);
                  }
            }
            sx={{
              ...rowIconButtonSx,
              color: layer.locked ? "rgba(0,0,0,0.8)" : "rgba(0,0,0,0.45)",
            }}
          >
            {layer.locked ? <Lock size={15} /> : <LockOpen size={15} />}
          </Box>
        ) : null}
        <Box
          component="button"
          type="button"
          title={deleteLabel}
          aria-label={deleteLabel}
          tabIndex={isOverlay ? -1 : undefined}
          onClick={
            isOverlay
              ? undefined
              : (event) => {
                  event.stopPropagation();
                  onDelete(layer.id);
                }
          }
          sx={rowIconButtonSx}
        >
          <Trash2 size={15} />
        </Box>
      </>
    )}
    <Radio
      checked={selected}
      onClick={isOverlay ? undefined : (event) => event.stopPropagation()}
      onChange={isOverlay ? undefined : (event) => onSelect(layer.id, event)}
      size="small"
      tabIndex={isOverlay ? -1 : undefined}
      sx={{
        p: 0.5,
        color: "rgba(0,0,0,0.22)",
        "&.Mui-checked": { color: SELECTED_BORDER },
      }}
    />
  </>
);

const layerRowSx = ({ selected, isDragging, isOverlay, isHidden }) => ({
  width: "100%",
  display: "flex",
  alignItems: "center",
  gap: 1,
  px: 1,
  py: 0.875,
  border: selected
    ? `1.5px solid ${SELECTED_BORDER}`
    : "1px solid rgba(0,0,0,0.08)",
  borderRadius: 2,
  bgcolor: "#fff",
  cursor: isOverlay ? "grabbing" : "pointer",
  textAlign: "left",
  opacity: isDragging && !isOverlay ? 0.4 : isHidden ? 0.45 : 1,
  boxShadow: isOverlay ? "0 8px 20px rgba(17, 24, 39, 0.16)" : "none",
  zIndex: isDragging || isOverlay ? 2 : 1,
  position: "relative",
  pointerEvents: isOverlay ? "none" : "auto",
  ...layerListNoNativeSelectSx,
});

const SortableLayerRow = ({
  layer,
  selected,
  onSelect,
  onRename,
  onDuplicate,
  onDelete,
  onToggleLock,
  onToggleVisible,
  backgroundLabel,
  fallbackName,
  duplicateLabel,
  deleteLabel,
  lockLabel,
  unlockLabel,
  hideLabel,
  showLabel,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: layer.id });
  const name =
    layer.name || (layer.isBackground ? backgroundLabel : fallbackName);

  return (
    <Box
      ref={setNodeRef}
      style={sortableLayerRowStyle({ transform, transition, isDragging })}
      onMouseDown={preventNativeTextSelectOnAdditivePointer}
      onClick={(event) => onSelect(layer.id, event)}
      sx={layerRowSx({
        selected,
        isDragging,
        isOverlay: false,
        isHidden: layer.visible === false,
      })}
    >
      <LayerRowContent
        layer={layer}
        name={name}
        selected={selected}
        isDragging={isDragging}
        handleRef={setActivatorNodeRef}
        handleAttributes={attributes}
        handleListeners={listeners}
        onSelect={onSelect}
        onRename={onRename}
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        onToggleLock={onToggleLock}
        onToggleVisible={onToggleVisible}
        duplicateLabel={duplicateLabel}
        deleteLabel={deleteLabel}
        lockLabel={lockLabel}
        unlockLabel={unlockLabel}
        hideLabel={hideLabel}
        showLabel={showLabel}
      />
    </Box>
  );
};

const OverlayLayerRow = ({
  layer,
  name,
  selected,
  onToggleLock,
  onToggleVisible,
  duplicateLabel,
  deleteLabel,
  lockLabel,
  unlockLabel,
  hideLabel,
  showLabel,
  width,
}) => (
  <Box
    sx={{
      ...layerRowSx({
        selected,
        isDragging: false,
        isOverlay: true,
        isHidden: layer.visible === false,
      }),
      width: width || "100%",
    }}
  >
    <LayerRowContent
      layer={layer}
      name={name}
      selected={selected}
      isOverlay
      onToggleLock={onToggleLock}
      onToggleVisible={onToggleVisible}
      duplicateLabel={duplicateLabel}
      deleteLabel={deleteLabel}
      lockLabel={lockLabel}
      unlockLabel={unlockLabel}
      hideLabel={hideLabel}
      showLabel={showLabel}
    />
  </Box>
);

const LayersSidebar = ({
  layers = [],
  selectedLayerId,
  selectedLayerIds = [],
  onSelectLayer,
  isAnalyzing,
  errorMessage,
  onAnalyze,
  onAddLayerUrls,
  onAddTextLayer = () => {},
  onReorderLayers = () => {},
  onRenameLayer = () => {},
  onDuplicateLayer = () => {},
  onDeleteLayer = () => {},
  onToggleLock,
  onToggleVisible,
  isLoadingSnapshot = false,
}) => {
  const { t } = useTranslationApp();
  const [isAddingLayer, setIsAddingLayer] = useState(false);
  const [isAssetsGalleryOpen, setIsAssetsGalleryOpen] = useState(false);
  const [addMenuEl, setAddMenuEl] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [overlayWidth, setOverlayWidth] = useState(null);
  const hasLayers = layers.length > 0;

  const onSelectGalleryItems = async (selectedItems) => {
    const urlList = (selectedItems || [])
      .map((item) => item?.url)
      .filter(Boolean);
    if (!urlList.length || typeof onAddLayerUrls !== "function") {
      return;
    }
    setIsAddingLayer(true);
    try {
      await onAddLayerUrls(urlList);
    } finally {
      setIsAddingLayer(false);
    }
  };

  const listLayers = useMemo(() => [...layers].reverse(), [layers]);
  const sortableIds = useMemo(
    () => listLayers.map((layer) => layer.id),
    [listLayers]
  );
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  const backgroundLabel = t(MODAL_IMAGE_EDIT_LAYERS_BACKGROUND);
  const duplicateLabel = t(TEMPLATES_DUPLICATE);
  const deleteLabel = t(TEMPLATES_DELETE);
  const lockLabel = t(TEMPLATES_LOCK_LAYER);
  const unlockLabel = t(TEMPLATES_UNLOCK_LAYER);
  const hideLabel = t(TEMPLATES_HIDE_LAYER);
  const showLabel = t(TEMPLATES_SHOW_LAYER);
  const activeLayerIndex = listLayers.findIndex(
    (layer) => layer.id === activeId
  );
  const activeLayer =
    activeLayerIndex >= 0 ? listLayers[activeLayerIndex] : null;
  const activeLayerName = activeLayer
    ? activeLayer.name ||
      (activeLayer.isBackground
        ? backgroundLabel
        : t(MODAL_IMAGE_EDIT_LAYERS_FALLBACK_NAME, {
            index: listLayers.length - activeLayerIndex,
          }))
    : "";

  const clearDragState = () => {
    setActiveId(null);
    setOverlayWidth(null);
  };

  const onDragStart = ({ active }) => {
    setActiveId(active.id);
    setOverlayWidth(active.rect.current.initial?.width ?? null);
  };

  const onDragEnd = (event) => {
    const { active, over } = event;
    clearDragState();
    if (!over || active.id === over.id) return;

    const oldIndex = sortableIds.indexOf(active.id);
    const newIndex = sortableIds.indexOf(over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    onReorderLayers(arrayMove(sortableIds, oldIndex, newIndex));
  };

  return (
    <Box
      onMouseDown={preventNativeTextSelectOnAdditivePointer}
      onSelectStart={preventLayerListNativeSelectStart}
      sx={{
        flex: 1,
        minHeight: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        ...layerListNoNativeSelectSx,
      }}
    >
      <Box
        data-layer-list-scroll=""
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowAnchor: "none",
          px: 2,
          py: 2,
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            height: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              mb: 0.5,
              flexShrink: 0,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                letterSpacing: 0.6,
                textTransform: "uppercase",
                color: "text.secondary",
              }}
            >
              {t(MODAL_IMAGE_EDIT_LAYERS_LIST_TITLE)}
            </Typography>
            <Box
              component="button"
              type="button"
              aria-busy={isAddingLayer}
              disabled={isAddingLayer || isAnalyzing}
              onClick={(event) => {
                if (isAddingLayer || isAnalyzing) return;
                setAddMenuEl(event.currentTarget);
              }}
              sx={{
                color: "#111",
                fontSize: 13,
                fontWeight: 700,
                cursor: isAddingLayer || isAnalyzing ? "default" : "pointer",
                whiteSpace: "nowrap",
                userSelect: "none",
                border: "none",
                bgcolor: "transparent",
                p: 0,
                fontFamily: "inherit",
                minWidth: 16,
                minHeight: 16,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "flex-end",
                opacity: isAnalyzing ? 0.4 : 1,
                pointerEvents: isAddingLayer || isAnalyzing ? "none" : "auto",
              }}
            >
              {isAddingLayer ? (
                <CircularProgress size={14} sx={{ color: "#111" }} />
              ) : (
                `+ ${t(MODAL_IMAGE_EDIT_LAYERS_ADD)}`
              )}
            </Box>
          </Box>
          <Menu
            anchorEl={addMenuEl}
            open={Boolean(addMenuEl)}
            onClose={() => setAddMenuEl(null)}
          >
            <MenuItem
              onClick={() => {
                setAddMenuEl(null);
                onAddTextLayer();
              }}
            >
              {t(TEMPLATES_PRODUCE_ADD_TEXT)}
            </MenuItem>
            <MenuItem
              onClick={() => {
                setAddMenuEl(null);
                setIsAssetsGalleryOpen(true);
              }}
            >
              {t(TEMPLATES_PRODUCE_ADD_IMAGE)}
            </MenuItem>
          </Menu>
          {hasLayers ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              autoScroll={layerListAutoScroll}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              onDragCancel={clearDragState}
            >
              <SortableContext
                items={sortableIds}
                strategy={verticalListSortingStrategy}
              >
                {listLayers.map((layer, index) => (
                  <SortableLayerRow
                    key={layer.id}
                    layer={layer}
                    selected={
                      selectedLayerIds.length
                        ? selectedLayerIds.includes(layer.id)
                        : selectedLayerId === layer.id
                    }
                    onSelect={onSelectLayer}
                    onRename={onRenameLayer}
                    onDuplicate={onDuplicateLayer}
                    onDelete={onDeleteLayer}
                    onToggleLock={onToggleLock}
                    onToggleVisible={onToggleVisible}
                    backgroundLabel={backgroundLabel}
                    fallbackName={t(MODAL_IMAGE_EDIT_LAYERS_FALLBACK_NAME, {
                      index: listLayers.length - index,
                    })}
                    duplicateLabel={duplicateLabel}
                    deleteLabel={deleteLabel}
                    lockLabel={lockLabel}
                    unlockLabel={unlockLabel}
                    hideLabel={hideLabel}
                    showLabel={showLabel}
                  />
                ))}
              </SortableContext>
              <DragOverlay zIndex={2000} style={{ cursor: "grabbing" }}>
                {activeLayer ? (
                  <OverlayLayerRow
                    layer={activeLayer}
                    name={activeLayerName}
                    selected={selectedLayerId === activeLayer.id}
                    onToggleLock={onToggleLock}
                    onToggleVisible={onToggleVisible}
                    duplicateLabel={duplicateLabel}
                    deleteLabel={deleteLabel}
                    lockLabel={lockLabel}
                    unlockLabel={unlockLabel}
                    hideLabel={hideLabel}
                    showLabel={showLabel}
                    width={overlayWidth}
                  />
                ) : null}
              </DragOverlay>
            </DndContext>
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flex: 1,
                minHeight: 160,
              }}
            >
              <Box sx={{ textAlign: "center", maxWidth: 320 }}>
                <Box
                  sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      bgcolor: "#000",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Layers size={24} color="#fff" />
                  </Box>
                </Box>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: "#000", mb: 0.5 }}
                >
                  {errorMessage
                    ? t(MODAL_IMAGE_EDIT_LAYERS_ERROR_TITLE)
                    : t(MODAL_IMAGE_EDIT_LAYERS_EMPTY_TITLE)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {errorMessage
                    ? t(MODAL_IMAGE_EDIT_LAYERS_ERROR_DESCRIPTION)
                    : isAnalyzing
                      ? t(MODAL_IMAGE_EDIT_LAYERS_ANALYZING)
                      : t(MODAL_IMAGE_EDIT_LAYERS_EMPTY_DESCRIPTION)}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {hasLayers || isAnalyzing ? null : (
        <Box sx={{ px: 2, py: 2, flexShrink: 0 }}>
          <Button
            color="primary"
            fullWidth
            disabled={isLoadingSnapshot || !onAnalyze}
            onClick={onAnalyze}
          >
            {errorMessage ? t(BUTTON_TRY_AGAIN) : t(BUTTON_ANALYZE)}
          </Button>
        </Box>
      )}
      {isAssetsGalleryOpen ? (
        <ModalGalleryAssets
          modalOpen={isAssetsGalleryOpen}
          multiple
          onCloseModal={() => setIsAssetsGalleryOpen(false)}
          onCallbackSelectedItems={onSelectGalleryItems}
        />
      ) : null}
    </Box>
  );
};

export default LayersSidebar;
